"use client";

import CommonImage from "@/components/common/image/CommonImage";
import { Modal } from "antd";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import React, { useState, useEffect } from "react";
import { FaCirclePlay } from "react-icons/fa6";
import HomePageService from "@/services/api/HomeService";
import { FaPhoneVolume, FaYoutube } from "react-icons/fa6";
import { FaFacebookF } from "react-icons/fa";
import { useSelector } from "react-redux";
import CommonService from "@/services/api/CommonService";
import GamificationSection from "../home/components/GamificationSection";

const defaultFeatures = [
  {
    title: "শিক্ষার্থী-কেন্দ্রিকতা",
    description:
      "প্রতিটি পাঠ পরিকল্পনা ও উদ্যোগ শিক্ষার্থীর চাহিদা, আগ্রহ ও উন্নতির দিক  মাথায় রেখে সাজানো।",
    img: "/images/about/about-item-1.avif",
  },
  {
    title: "গেমিফাইড আনন্দময় শিক্ষা",
    description:
      "গেমের মতো আকর্ষণীয় কনটেন্টের মাধ্যমে শেখাকে করি মজার ও মনোযোগ-আকর্ষণকারী।",
    img: "/images/about/about-item-3.avif",
  },
  {
    title: "সৃজনশীলতা ও উদ্ভাবন",
    description:
      "শিক্ষার্থীদের চিন্তাশক্তি জাগ্রত করে নতুন কিছু ভাবতে ও তৈরি করতে উৎসাহ দেওয়া।",
    img: "/images/about/about-item-4.avif",
  },
  {
    title: "মূল্যবোধ ও নৈতিকতা",
    description:
      "মানবিক মূল্যবোধের উপর ভিত্তি করে পরিচালিত হয় প্রতিটি শিক্ষা কার্যক্রম।",
    img: "/images/about/about-item-5.avif",
  },
  {
    title: "সাশ্রয়ী কিন্তু উচ্চমানসম্পন্ন শিক্ষা",
    description:
      "সবার নাগালে থাকা ফিতে প্রিমিয়াম মানের কোচিং ও একাডেমিক সহায়তা নিশ্চিতে কাজ করে যাচ্ছি ।",
    img: "/images/about/about-item-6.avif",
  },
  {
    title: "দ্রুত ও কার্যকর বাস্তবায়ন",
    description:
      "পরিকল্পনা থেকে বাস্তবায়নে আমরা দ্রুত সিদ্ধান্ত নেই এবং গুণগত মান বজায় রেখে কাজ সম্পন্ন করি।",
    img: "/images/about/about-item-1.avif",
  },
];

/** Background colour classes for items, looping if > length */
const bgClasses = [
  "bg-blue",
  "bg-orange",
  "bg-purple",
  "bg-green",
  "bg-vivid",
  "bg-blue",
];

/** Helper to convert 1→০১, 2→০২ ... */
const toBanglaSerial = (n) =>
  n
    .toString()
    .padStart(2, "0")
    .replace(/\d/g, (d) => "০১২৩৪৫৬৭৮৯"[d]);

export default function AboutUs() {
  const setting = useSelector((state) => state.setting.data);

  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [bannerFeatures, setBannerFeatures] = useState([]);
  const [about, setAbout] = useState({});
  const [loading, setLoading] = useState(false);

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => setIsModalOpen(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // const response = await HomePageService.getBannerFeatures();
      // if (response) setBannerFeatures(response);

      const aboutRes = await CommonService.getAbout();
      if (aboutRes) setAbout(aboutRes);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  /** Feature list (fallback to default) */
  const features =
    about?.feature_title_description?.length > 0
      ? about.feature_title_description
      : defaultFeatures;

  const leftFeatures = features?.slice(0, 3);
  const rightFeatures = features?.slice(3, 6);

  /** Render single feature item */
  const renderFeature = (item, idx) => (
    <div className={`item-list ${bgClasses[idx % bgClasses.length]}`} key={idx}>
      <div className="image-wrap leading-0">
        <CommonImage
          src={item.img ?? defaultFeatures[idx].img}
          alt="Icon"
          className="icon"
        />
      </div>
      <p className="serial-nmbr">{toBanglaSerial(idx + 1)}</p>
      <div className="list-text">
        <h3 className="title">{item.title}</h3>
        <p className="sub-title">
          {(item?.description ?? "").replace(/<[^>]+>/g, "")}
          </p>
      </div>
    </div>
  );

  const centerDesktop = about?.center_image_url ?? "/images/about/center.avif";
  const centerMobile =
    about?.center_image_mobile_url ?? "/images/about/center-mb.avif";
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section about-hero_section section_short-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 hero-content">
            {/* Left */}
            <div className="left-part">
              <div className="top-btn">
                <div className="image-wrap">
                  <CommonImage
                    src="/images/icon/about.avif"
                    alt="Image"
                    className="icon"
                  />
                </div>
                <span>ফোকাস সম্পর্কে</span>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="hero-text"
              >
                <h1 className="main-title">
                  <span className="highlighted">{about?.title}</span>
                  {about?.title2 && (
                    <>
                      <br />
                      {about.title2}
                    </>
                  )}
                </h1>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
              
                <p className="short-des">{about?.short_description?.replace(/<[^>]+>/g, '')}</p>
              </motion.div>
            </div>

            {/* Right */}
            <div className="right-part">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hero-image relative"
              >
                <CommonImage
                  src={about?.video_id?`https://img.youtube.com/vi/${about.video_id}/maxresdefault.jpg`:"/images/home-page/about-us.avif"}
                  alt="Hero Image"
                  className="hero-img"
                />

              <div className="hero-image-playbtn">
                <a onClick={showModal} className="play-btn">
                  <FaCirclePlay className="play-icon" />
                </a>
              </div>

                {/* <button
                  onClick={showModal}
                  className="play-btn absolute inset-0 flex items-center justify-center"
                >
                  <FaCirclePlay className="play-icon" />
                </button> */}
              </motion.div>
            </div>
          </div>
        </div>
        {/* Video Modal */}
        <Modal
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          centered
          width={800}
          className="video-modal"
          styles={{ body: { padding: 0 } }}
          destroyOnHidden
        >
          <div className="aspect-video w-full">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${
                about?.video_id ?? "dQw4w9WgXcQ"
              }?autoplay=1`}
              title="YouTube Video"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </Modal>
      </section>

      {/* Basics-part: Features & Center Image */}
      <section className="Basics-part_section bg-white [.dark_&]:bg-d-oxford-blue">
        <div className="container">
          <div className="grid grid-cols-12 gap-6 items-center justify-items-center">
            {/* Left 3 */}
            <div className="2xl:col-span-5 lg:col-span-6 col-span-12 2xl:order-1 order-2">
              {leftFeatures.map(renderFeature)}
            </div>

            {/* Center */}
            <div className="2xl:col-span-2 lg:col-span-12 col-span-12 2xl:order-2 order-1">
              <div className="image-wrap leading-0">
                <CommonImage
                  src={centerDesktop}
                  alt="Center"
                  className="icon hidden lg:block"
                />
                <CommonImage
                  src={centerMobile}
                  alt="Center mobile"
                  className="icon block lg:hidden"
                />
              </div>
            </div>

            {/* Right 3 */}
            <div className="2xl:col-span-5 lg:col-span-6 col-span-12 2xl:order-3 order-3">
              {rightFeatures.map((item, idx) => renderFeature(item, idx + 3))}
            </div>
          </div>
        </div>
      </section>
     {/* <GamificationSection></GamificationSection> */}
      {/* Gamification== */}
      {/* <section className="gamification_section md:py-8 py-5">
        <div className="container">
          <div className="grid grid-cols-12 md:gap-6 gap-4 items-center">
            <div className="lg:col-span-5 col-span-12">
              <div className="image-part">
                <CommonImage
                  src={
                    setting?.gamification_image_url ??
                    "/images/home-page/gamifiction.avif"
                  }
                  className="gamification-thumbnail leading-0"
                  alt={"Image"}
                />
              </div>
            </div>
              
          </div>
        </div>
      </section> */}

      {/* Help=== */}
      <section
        className="help_section section_padding bg-white [.dark_&]:bg-d-oxford-blue"
        style={
          {
            // backgroundImage: "url('/images/home-page/help-bg-pattern.avif')",
          }
        }
      >
        <div className="container">
          <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-6 gap-4">
            <div
              className="help-card bg-pattern"
              style={{
                backgroundImage: "url('/images/home-page/pattern.avif')",
              }}
            >
              <h2 className="help-title">যেকোনো প্রয়োজনে আমাদেরকে কল করো</h2>
              {setting?.phone_1 && (
                <Link href={`tel:${setting.phone_1}`} className="help-button">
                  <span className="icon">
                    <FaPhoneVolume />
                  </span>{" "}
                  কল করো
                </Link>
              )}
            </div>

            <div
              className="help-card bg-pattern help-video-card"
              style={{
                backgroundImage: "url('/images/home-page/pattern.avif')",
              }}
            >
              <h2 className="help-title">ফ্রি ভিডিও দেখো</h2>
              {setting && (
                <Link
                  href={setting?.youtube ?? "#"}
                  className="help-button help-video-button"
                >
                  <span className="icon">
                    <FaYoutube />{" "}
                  </span>{" "}
                  ভিডিও দেখো
                </Link>
              )}
            </div>

            <div
              className="help-card bg-pattern"
              style={{
                backgroundImage: "url('/images/home-page/pattern.avif')",
              }}
            >
              <h2 className="help-title">
                ফোকাসের ফেসবুক গ্রুপে এখনিযুক্ত হও
              </h2>
              {setting && (
                <Link href={setting?.facebook ?? "#"} className="help-button">
                  <span className="icon fb-icon">
                    <FaFacebookF />
                  </span>
                  গ্রুপে যুক্ত হও{" "}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
